import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import type { TranslationToggleProps } from './TranslationToggle';

/**
 * PLAT-11877 — the YouTube Studio model for the transcript's languages: a
 * language is a place you go, not a toolbar filter. From the ONE
 * `TranslationToggle` element a host builds (every prop the host already
 * passes), this returns the three places it shows up:
 *
 *  - `toolbar`: a globe with the language count, for `headerExtras` — the
 *    toolbar's only language control; it opens the Languages panel.
 *  - `title`: the line naming the translation being read, with Review and a
 *    way back to the original — for the front of `belowHeader`. Nothing on
 *    the original, so an untranslated read looks exactly as before.
 *  - `panel`: the Languages list, over the transcript pane while open. The
 *    host renders it inside the pane's positioned container, and inside the
 *    same store provider as the transcript (the clip preview's clip-scoped
 *    stores).
 *
 * A host with a tab for languages (the clip preview's Languages tab) passes
 * `tab`: the list then always renders into that tab's pane, and the globe and
 * the list's back / pick move between the two tabs instead of opening and
 * closing an overlay.
 *
 * A plain function rather than a hook: the open state lives with the host
 * (`useState`), declared with its other state, so this can be called after
 * the host's early returns.
 */
export function placeLanguageControls(
  toggle: React.ReactElement<TranslationToggleProps>,
  panelOpen: boolean,
  setPanelOpen: (open: boolean) => void,
  tab?: {
    /** The Languages tab's pane, the portal target. */
    target: HTMLElement;
    /** Show the Languages tab. */
    open: () => void;
    /** Back to the Transcript tab. */
    close: () => void;
  },
): {
  toolbar: React.ReactElement;
  title: React.ReactElement;
  panel: React.ReactElement | null;
} {
  if (tab) {
    return {
      toolbar: React.cloneElement(toggle, {
        presentation: 'button',
        onOpenPanel: tab.open,
      }),
      title: React.cloneElement(toggle, { presentation: 'title' }),
      // The tab bar is the way back, so the list carries no back arrow.
      panel: createPortal(
        React.cloneElement(toggle, {
          presentation: 'panel',
          onClosePanel: tab.close,
          panelBack: false,
        }),
        tab.target,
      ),
    };
  }
  return {
    toolbar: React.cloneElement(toggle, {
      presentation: 'button',
      onOpenPanel: () => setPanelOpen(true),
    }),
    title: React.cloneElement(toggle, { presentation: 'title' }),
    panel: panelOpen ? (
      <LanguagesOverlay>
        {React.cloneElement(toggle, {
          presentation: 'panel',
          onClosePanel: () => setPanelOpen(false),
        })}
      </LanguagesOverlay>
    ) : null,
  };
}

// Covers the transcript pane (its host container must be positioned). Below
// the portaled menus, tooltips and modals; above the transcript text.
const LanguagesOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  flex-direction: column;
  background: var(--background);
`;
